import type { ReactElement } from 'react';
import { Modal, type ModalAction } from './Modal';

export interface ConflictDialogProps {
  open: boolean;
  /** Display name of the file that changed on disk. */
  fileName: string;
  /** Overwrite disk with the in-memory edits. */
  onKeepMine: () => void;
  /** Discard local edits and load the on-disk content. */
  onLoadExternal: () => void;
  /** Save the local edits under a new name (keep both). */
  onSaveAs: () => void;
  /** Dismiss without resolving (e.g. backdrop/Esc). */
  onClose: () => void;
}

/**
 * Three-way conflict resolver shown when a file changes on disk while it has
 * unsaved local edits (spec Task 35). "Load external" is the default/primary
 * action — the on-disk version is the freshest source of record.
 */
export function ConflictDialog({
  open,
  fileName,
  onKeepMine,
  onLoadExternal,
  onSaveAs,
  onClose,
}: ConflictDialogProps): ReactElement {
  // "Load external" is the default/primary (the on-disk version is the freshest
  // record). No explicit Cancel button — Esc/backdrop dismiss via onClose.
  const actions: ModalAction[] = [
    { id: 'keep-mine', label: 'Keep mine', onSelect: onKeepMine, accelerator: 'k' },
    { id: 'save-as', label: 'Save mine as…', onSelect: onSaveAs, accelerator: 's' },
    { id: 'load-external', label: 'Load external', onSelect: onLoadExternal, accelerator: 'l', variant: 'primary', isDefault: true },
  ];

  return (
    <Modal open={open} onClose={onClose} title="File changed on disk" actions={actions}>
      <p className="mp-modal__text">
        <strong>{fileName}</strong> was changed outside the editor, but you have unsaved
        edits. Choose how to resolve the conflict:
      </p>
      <ul className="mp-modal__list">
        <li>
          <strong>Keep mine</strong> — overwrite the file on disk with your edits.
        </li>
        <li>
          <strong>Save mine as…</strong> — keep both: save your edits to a new file and
          reload this tab from disk.
        </li>
        <li>
          <strong>Load external</strong> — discard your edits and load the on-disk
          version.
        </li>
      </ul>
    </Modal>
  );
}
